/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode } from 'react';
import {
  Box,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AvatarCard,
  ErrorBox,
  ErrorTitle,
  ErrorMessage,
  Link,
  OutlinedIcons,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { Conversation, TripMatch } from '@marageti/z4-sdk/lib/travel';
import {
  getInitials,
} from '../../utils/helpers';
import TripMatchStatusChip from '../chips/trip-match';
import { ReactComponent as UnreadChatIcon } from '../../assets/svg/unread-chat.svg';
import useCloudinaryUrl from '../../hooks/use-trip-photo';

const {
  OutlinedChatAltIcon,
} = OutlinedIcons;

const Card = ({
  children,
  sx = {} as SxProps,
}: { children: ReactNode, sx?: SxProps }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '12px',
      px: 4,
      py: 4,
      ...sx,
    }}
  >
    {children}
  </Box>
);

type ZicassoCareSpecialistCardProps = {
  travelerId: string
  tripMatchId?: string,
  tripRequestId?: string,
  conversationId?: string,
};

export const ZicassoCareSpecialistCard = ({
  travelerId,
  tripMatchId = undefined,
  tripRequestId = undefined,
  conversationId = undefined,
}: ZicassoCareSpecialistCardProps) => {
  const apiClient = useApiClient();
  const {
    data,
  } = useFetch<Conversation>(apiClient.conversationsClient.getConversation, [conversationId], !!conversationId);
  const messageState = data ? data.messageState : [];
  const assignedToMessageState = messageState.find((m) => m.personId !== '' && m.personId === travelerId);
  const hasNewMessage = assignedToMessageState?.hasNew || false;
  const link = tripMatchId ? `/trip-matches/${tripMatchId}/messages` : `/trip-requests/${tripRequestId}/messages`;
  return (
    <Link to={link} key={conversationId}>
      <Card
        sx={{
          paddingY: 1.75,
          paddingX: 3,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderColor: 'secondary.main',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <AvatarCard
            size={32}
            sx={{
              marginRight: 1,
              backgroundColor: 'secondary.main',
              color: 'white',
            }}
          >
            Z
          </AvatarCard>
          <Typography variant="bodyBold" sx={{ fontWeight: 700 }}>Zicasso Care Specialist</Typography>
        </Box>
        {(tripMatchId || tripRequestId) && (
          <Tooltip title="Chat with Zicasso">
            <>
              {hasNewMessage && <UnreadChatIcon />}
              {!hasNewMessage && <OutlinedChatAltIcon />}
            </>
          </Tooltip>
        )}
      </Card>
    </Link>
  );
};

type DetailedMatchCardProps = {
  tripMatch: TripMatch,
  travelerId: string,
};

export const DetailedMatchCard = ({
  tripMatch,
  travelerId,
}: DetailedMatchCardProps) => {
  const link = tripMatch.id ? `/trip-matches/${tripMatch.id}/messages` : `/trip-requests/${tripMatch.tripRequestId}/messages`;
  const partnerName = tripMatch.partnerName || '-';
  const assignedToMessageState = tripMatch.tripMessageState.find((m) => m.personId !== '' && m.personId === travelerId);
  const hasNewMessage = assignedToMessageState?.hasNew || false;

  return (
    <Stack direction="row" spacing={1.5}>
      <Link to={`/trip-matches/${tripMatch.id}`} sx={{ width: '100%' }}>
        <Card
          sx={{
            padding: 3,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            columnGap={1}
            rowGap={2.25}
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            alignContent="space-between"
            width="100%"
          >
            <Box display="flex" flexGrow={1} alignItems="center" minWidth={0}>
              <AvatarCard
                size={40}
                sx={{
                  marginRight: 1,
                }}
              >
                {getInitials(partnerName)}
              </AvatarCard>
              <Box>
                <Typography variant="bodyBold">{partnerName}</Typography>
                <Typography variant="caption">
                  Specialist:
                  {' '}
                  {tripMatch.assignedToName}
                </Typography>
              </Box>
            </Box>

            <Box>
              <TripMatchStatusChip tripMatch={tripMatch} size="medium" />
            </Box>
          </Stack>
        </Card>
      </Link>

      {tripMatch.travelerToPartnerId && (
        <Tooltip title={tripMatch.partnerName ? `Chat with ${tripMatch.partnerName}` : 'Chat'}>
          <span style={{ display: 'flex' }}>
            <Link to={link} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  padding: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <>
                  {hasNewMessage && <UnreadChatIcon />}
                  {!hasNewMessage && <OutlinedChatAltIcon />}
                </>
              </Card>
            </Link>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

const mobileDimensions = { width: 342, height: 310 };
const desktopDimensions = { width: 809, height: 870 };

// trip Image
export const TripHeroImage = ({
  photoId,
  sx = {} as SxProps,
}: { photoId: string, sx?: SxProps }) => {
  const isDesktopDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('desktop'));
  const desktopAspectRatio = 809 / 870;
  const mobileAspectRatio = 342 / 310;

  const [imageUrl, isLoading, error] = useCloudinaryUrl(photoId, isDesktopDown ? mobileDimensions : desktopDimensions);

  if (isLoading || error) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: '100%',
          borderRadius: '8px',
          paddingTop: { mobile: `${(1 / mobileAspectRatio) * 100}%`, desktop: `${(1 / desktopAspectRatio) * 100}%` },
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: { mobile: `${(1 / mobileAspectRatio) * 100}%`, desktop: `${(1 / desktopAspectRatio) * 100}%` },
        borderRadius: '8px',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {(!isLoading && !error) && (
      <img
        src={imageUrl}
        alt="Destination"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '8px',
        }}
      />

      )}
    </Box>
  );
};

export const TripError = () => (
  <Box width="450px" sx={{ margin: '0 auto' }}>
    <ErrorBox>
      <ErrorTitle>
        There was an error loading your trip.
      </ErrorTitle>
      <ErrorMessage>
        Please go back and try again
      </ErrorMessage>
    </ErrorBox>
  </Box>
);
